import firstValue from "../../helpers/first.value";
import Container from "../../components/element/container";
import { getProductCover } from "../../helpers/product.media";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import Icon from "../../components/element/icon";
import { DcLogo } from "../../assets/images";
import formatNumber from "../../helpers/format.number";
import { showToast } from "../../helpers/show.toast";
import {
  createFinance,
  createVso,
  getOrderList,
} from "../../redux/reducer/order.reducer";
import PurchaseRecord from "./components/purchase.record";
import IdentityDetailsModal from "./modals/identity.details.modal";
import InsuranceRequestModal from "./modals/insurance.request.modal";
import PaymentMethodModal from "./modals/payment.method.modal";
import useRequireAuth from "../../components/hook/use.require.auth";
import VsoSigning from "./components/vso.signing";
import PurchaseProgress from "./components/purchase.progress";
import PurchaseStepReviewModal from "./modals/purchase.step.review.modal";
import { getSubmittedPurchaseSteps } from "../../helpers/purchase.steps";
import { getSignedPurchaseDocuments } from "../../helpers/order.documents";

const stageNumber = {
  reserved: 1,
  financing: 2,
  insurance: 3,
};


function getOrderProduct(order) {
  const item = firstValue(order?.order_items?.[0], order?.items?.[0]);

  return firstValue(
    order?.product,
    order?.reservation?.product,
    item?.product,
    item,
    {},
  );
}

function getOrderId(order) {
  return firstValue(order?.id, order?.order_id, order?.uuid);
}

function getOrderProductId(order) {
  const product = getOrderProduct(order);

  return firstValue(
    order?.product_id,
    order?.reservation?.product_id,
    product?.id,
    product?.product_id,
  );
}

function displayText(value, fallback = "-") {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return firstValue(value?.name, value?.label, value?.title, fallback);
}

function displayStatus(value) {
  const status = displayText(value, "Pending").replace(/[_-]+/g, " ");

  return status.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function displayDate(value) {
  if (!value) return "-";

  const date = dayjs(value);
  return date.isValid() ? date.format("D MMM YYYY") : String(value);
}

function displayMoney(value, fallback = "-") {
  if (value === undefined || value === null || value === "") return fallback;

  if (
    typeof value === "string" &&
    value.trim().toUpperCase().startsWith("RM")
  ) {
    return value;
  }

  try {
    return `RM ${formatNumber(value)}`;
  } catch {
    return String(value);
  }
}

function normalizeOrder(order) {
  const product = getOrderProduct(order);
  const media = firstValue(product?.media, order?.media, []);
  const mainImage = getProductCover(media);
  const orderId = getOrderId(order);

  const reservationFee = firstValue(
    order?.amount_due,
    order?.reservation_fee,
    order?.payment_amount,
    order?.total,
    5000,
  );

  return {
    orderId,
    productId: getOrderProductId(order),
    name: displayText(
      firstValue(product?.name, order?.product_name, order?.name),
      "Reserved Vehicle",
    ),
    reservationId: displayText(
      firstValue(
        order?.reservation_id,
        order?.reservation?.reference_no,
        order?.reference_no,
        order?.order_no,
        orderId,
      ),
    ),
    reservationDate: displayDate(
      firstValue(
        order?.reserved_at,
        order?.reservation_date,
        order?.reservation?.created_at,
        order?.created_at,
        order?.date,
      ),
    ),
    sellingPrice: displayMoney(
      firstValue(order?.selling_price, product?.price, order?.subtotal),
    ),
    sellingPriceValue: firstValue(
      order?.selling_price,
      product?.price,
      order?.subtotal,
      0,
    ),
    conditioningCost: displayMoney(
      firstValue(order?.conditioning_cost, order?.reconditioning_cost),
    ),
    warrantyCost: displayMoney(order?.warranty_cost),
    paymentAmount: displayMoney(reservationFee, "RM 5,000"),
    status: displayStatus(order?.status),
    paymentStatus: order?.payment_status,
    image: firstValue(mainImage?.url, DcLogo),
    hasImage: Boolean(mainImage?.url),
    source: order,
  };
}

function extractOrders(response) {
  const data = response?.data;

  if (Array.isArray(data?.aaData)) return data.aaData;
  if (Array.isArray(data?.orders)) return data.orders;
  if (Array.isArray(data)) return data;

  return [];
}

export default function PurchaseJourney() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, requireAuth } = useRequireAuth();

  const requestStage = searchParams.get("stage") || "all";

  const stage = ["financing", "insurance", "delivery"].includes(requestStage)
    ? requestStage
    : "all";

  useEffect(() => {
    if (searchParams.has("stage") && stage === "all") {
      const params = new URLSearchParams(searchParams);
      params.delete("stage");
      setSearchParams(params, { replace: true, state: location.state });
    }
  }, [searchParams, setSearchParams, stage, location.state]);

  const initialPaymentRequest = useRef(
    location.state?.paymentAfterReserve || null,
  );

  const initialLoadStarted = useRef(null);

  const [orderId, setOrderId] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [insuranceOpen, setInsuranceOpen] = useState(false);
  const [vsoSigningOpen, setVsoSigningOpen] = useState(false);
  const [signingTab, setSigningTab] = useState("spa");
  const [signedAgreements, setSignedAgreements] = useState({});
  const [signedVso, setSignedVso] = useState({});
  const [loadError, setLoadError] = useState("");
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [submittedSteps, setSubmittedSteps] = useState({});
  const [review, setReview] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [inlineReview, setInlineReview] = useState(null);
  const saveSubmittedStep = (id, key, data) => {
    setSubmittedSteps((current) => ({
      ...current,
      [id]: { ...current[id], [key]: data },
    }));
  };

  const { createLoading, orderList, orderListLoading } = useSelector(
    (state) => state.order,
  );

  const normalizedPurchases = useMemo(
    () => (Array.isArray(orderList) ? orderList.map(normalizeOrder) : []),
    [orderList],
  );

  const routePath = `${location.pathname}${location.search}`;

  const refreshOrders = useCallback(
    async (handleReservationPayment = false) => {
      setLoadError("");

      try {
        const response = await dispatch(
          getOrderList({
            ...(stage === "all" ? {} : { status: stage }),
          }),
        ).unwrap();

        const orders = extractOrders(response);

        if (handleReservationPayment && initialPaymentRequest.current) {
          const request = initialPaymentRequest.current;

          const requestedOrder =
            orders.find(
              (order) =>
                request.orderId != null &&
                String(getOrderId(order)) === String(request.orderId),
            ) ||
            orders.find(
              (order) =>
                request.productId != null &&
                String(getOrderProductId(order)) === String(request.productId),
            ) ||
            orders[0];

          initialPaymentRequest.current = null;

          navigate(routePath, {
            replace: true,
            state: null,
          });

          if (requestedOrder) {
            setPaymentOrder(normalizeOrder(requestedOrder));
          } else {
            showToast(
              "Order is not ready yet",
              "Your reservation succeeded. Refresh the purchases list to continue payment.",
              "warning",
            );
          }
        }
      } catch (error) {
        setLoadError(
          error?.response?.data?.message || "Unable to load your purchases.",
        );
      } finally {
        setIsInitialLoading(false);
      }
    },
    [dispatch, navigate, routePath, stage],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setIsInitialLoading(false);
      requireAuth();
      return;
    }

    if (initialLoadStarted.current === stage) return;

    initialLoadStarted.current = stage;
    setIsInitialLoading(true);
    refreshOrders(Boolean(initialPaymentRequest.current));
  }, [isAuthenticated, refreshOrders, requireAuth, stage]);

  const moveToStage = useCallback(
    (nextStage) => {
      if (stage === "all") {
        refreshOrders();
      } else {
        setSearchParams({ stage: nextStage });
      }

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    [setSearchParams, stage, refreshOrders],
  );

  const afterSigning = (orderId, justSigned) => {
    const saved = getSignedPurchaseDocuments(
      normalizedPurchases.find(
        (purchase) => String(purchase.orderId) === String(orderId),
      ),
      {
        spa: signedAgreements[orderId],
        vso: signedVso[orderId],
      },
    );
    const agreementDone = justSigned === "spa" || Boolean(saved.spa);
    const vsoDone = justSigned === "vso" || Boolean(saved.vso);

    if (agreementDone && vsoDone) {
      setVsoSigningOpen(false);
      setInlineReview(null);
      // Only the financing stage moves on; signing again from a later stage
      // stays where it is.
      if (stage === "financing" || stage === "all") moveToStage("insurance");
      return;
    }

    setSigningTab(agreementDone ? "vso" : "spa");
    setVsoSigningOpen(true);
  };

  const handleAgreementSubmit = async (orderId, signedDocument, signer) => {
    try {
      const formData = new FormData();
      formData.append("sale_purchase_agreement", signedDocument);

      await dispatch(
        createVso({
          order_id: orderId,
          data: formData,
        }),
      ).unwrap();

      showToast(
        "Agreement signed",
        "Your signed Sale and Purchase Agreement has been submitted.",
        "success",
      );
      setSignedAgreements((current) => ({
        ...current,
        [orderId]: signedDocument,
      }));
      saveSubmittedStep(orderId, "agreement", {
        ...signer,
        sale_purchase_agreement: signedDocument,
      });
      afterSigning(orderId, "spa");
    } catch (error) {
      showToast(
        "Unable to submit the agreement",
        error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          "Please try again.",
        "error",
      );
      throw error;
    }
  };

  const handleFinanceSubmit = async (
    orderId,
    signedDocument,
    signer,
    buyerType,
  ) => {
    if (!buyerType) {
      showToast(
        "Purchase method required",
        "Please submit your purchase method in the Identity Details step first.",
        "warning",
      );

      return;
    }

    try {
      await dispatch(
        createFinance({
          order_id: orderId,
          buyer_type: buyerType,
        }),
      ).unwrap();

      const vsoFormData = new FormData();
      vsoFormData.append("vso_document", signedDocument);

      await dispatch(
        createVso({
          order_id: orderId,
          data: vsoFormData,
        }),
      ).unwrap();

      showToast(
        "VSO signed successfully",
        "Your purchase method and signed VSO have been submitted.",
        "success",
      );
      saveSubmittedStep(orderId, "finance", {
        buyer_type: buyerType,
        ...signer,
        vso_document: signedDocument,
      });
      setSignedVso((current) => ({ ...current, [orderId]: signedDocument }));
      afterSigning(orderId, "vso");
    } catch (error) {
      showToast(
        "Unable to complete VSO signing",
        error?.response?.data?.message ||
          error?.data?.message ||
          error?.message ||
          "Please try again.",
        "error",
      );
      throw error;
    }
  };

  const visiblePurchases = useMemo(
    () =>
      stage === "complete"
        ? normalizedPurchases.filter((purchase) =>
            ["completed", "paid", "delivered"].includes(
              purchase.status.toLowerCase(),
            ),
          )
        : normalizedPurchases,
    [normalizedPurchases, stage],
  );

  // Registration details can only be filled in once the reservation fee has
  // actually cleared.
  const isPaid = (purchase) =>
    ["paid", "success", "successful", "completed", "captured"].includes(
      String(purchase?.paymentStatus || "").toLowerCase(),
    );

  // The buyer's purchase method is captured with the identity step, so read it
  // back from there (or from the order once the API returns it).
  const buyerTypeOf = (purchase) =>
    firstValue(
      purchase.source?.buying_method,
      submittedSteps[purchase.orderId]?.identity_details?.buyer_type,
      purchase.source?.buying_method,
      purchase.source?.finance_type,
      purchase.source?.identity?.buyer_type,
      "",
    );

  // Signing only opens once the API flags the VSO as requested. Older orders
  // do not carry the flag at all, so only an explicit false closes signing.
  const isVsoRequested = (purchase) => {
    const value = purchase.source?.vso_requested;

    if (value === undefined || value === null) return true;

    return [true, 1, "1", "true"].includes(value);
  };

  // What is still owed after everything collected so far, and the 10% a loan
  // buyer puts down.
  const amountOf = (purchase, key) => Number(purchase.source?.[key]) || 0;
  const remainingBalanceOf = (purchase) =>
    Math.max(
      0,
      amountOf(purchase, "total_car_price") -
        amountOf(purchase, "total_collection"),
    );
  const downPaymentOf = (purchase) =>
    amountOf(purchase, "total_car_price") * 0.1;

  const bankDetails = [
    ["Bank Account Name", "DCH AUTOMOBILE SDN BHD"],
    ["Bank Name & Address", "STANDARD CHARTERED BANK MALAYSIA"],
  ];
  const bankAccountNo = "708409939989";

  const copyAccountNo = async () => {
    try {
      await navigator.clipboard.writeText(bankAccountNo);
      showToast("Copied", "Bank account number copied.", "success");
    } catch {
      showToast("Unable to copy", "Please copy the number manually.", "error");
    }
  };

  const taskCopy = {
    reserved: {
      title: "Provide Registration Details",
      subtitle:
        "Information you provide will be applied to the vehicle’s registration",
    },
    financing: {
      title: "Are You Planning to Lease or Finance Your Vehicle?",
      subtitle: "Loan, Lease and Self-Arranged Financing options are available",
    },
    insurance: {
      title: "Provide Proof of Insurance",
      subtitle:
        "Don't forget to transfer your NCD to lower your insurance premium",
    },
  };

  return (
    <>
      <main className={`purchase-journey stage-${stage}`}>
        <header className="purchase-page-header">
          <button
            type="button"
            className="_button purchase-page-back"
            onClick={() => navigate("/profile")}
            aria-label="Back to profile"
          >
            <Icon icon="ep:back" />
          </button>

          <h1 className="_label purchase-page-title">
            {t("My Purchases Listing")}
          </h1>

          <article />
        </header>

        <section className="purchase-list">
          {isInitialLoading || orderListLoading ? (
            <Container tag="div" className="purchase-list-loading" aria-label="Loading orders">
              {Array.from({ length: 2 }, (_, index) => (
                <span key={index} />
              ))}
            </Container>
          ) : loadError ? (
            <Container tag="div" className="purchase-list-state">
              <Icon icon="solar:danger-triangle-linear" />

              <p>{loadError}</p>

              <button type="button" onClick={() => refreshOrders(false)}>
                {t("Try Again")}
              </button>
            </Container>
          ) : visiblePurchases.length === 0 ? (
            <Container tag="div" className="purchase-list-state">
              <Icon icon="solar:clipboard-list-linear" />

              <p>{t("No purchases found.")}</p>
            </Container>
          ) : (
            visiblePurchases.map((purchase, index) => {
              const steps = getSubmittedPurchaseSteps(
                purchase,
                submittedSteps[purchase.orderId],
              );
              const stage =
                ["reserved", "financing", "insurance"][
                  steps.findIndex((step) => !step.submitted)
                ] || "delivery";
              const signedDocuments = getSignedPurchaseDocuments(purchase, {
                spa: signedAgreements[purchase.orderId],
                vso: signedVso[purchase.orderId],
              });
              // One record is open at a time. The first one starts open on
              // every stage except delivery, where nothing expands until the
              // buyer taps a record.
              const isActiveEntry =
                isPaid(purchase) &&
                (expandedOrder
                  ? String(expandedOrder) === String(purchase.orderId)
                  : stage !== "delivery" && index === 0);
              const canSign = isVsoRequested(purchase);
              // Without the request the buyer either has nothing to sign yet,
              // or has already submitted once and is being asked again.
              const vsoNeedsSignature = canSign && (
                !signedDocuments.vso ||
                ([true, 1, "1", "true"].includes(purchase.source?.vso_requested) &&
                  !signedVso[purchase.orderId])
              );
              const requireSign = vsoNeedsSignature && steps[1].submitted;
              const signingLocked = !canSign && !steps[1].submitted;
              const progressSteps = requireSign
                ? steps.map((step) =>
                    step.number === 2
                      ? { ...step, alert: true, actionLabel: "Require sign" }
                      : step,
                  )
                : steps;
              const openReview = (number) => {
                if (number !== 2) {
                  setReview({ purchase, number });
                  return;
                }

                setOrderId(purchase.orderId);

                // A signature that is being asked for again opens on the VSO
                // tab.
                setSigningTab(requireSign || signedDocuments.spa ? "vso" : "spa");

                // Step 2 is reviewed in place, under the steps, instead of in
                // a modal.
                setInlineReview((current) =>
                  String(current) === String(purchase.orderId)
                    ? "none"
                    : purchase.orderId,
                );
              };
              const signingTabs = [
                [
                  "spa",
                  t("Signing S&P Agreement"),
                  Boolean(signedDocuments.spa),
                ],
                ["vso", t("Signing VSO"), Boolean(signedDocuments.vso) && !vsoNeedsSignature],
              ];
              // The S&P agreement comes first until it is signed, then the
              // VSO. By delivery there is nothing left to sign, so the VSO is
              // the one worth opening on.
              const defaultSigningTab =
                stage === "delivery" || signedDocuments.spa ? "vso" : "spa";
              const activeTab =
                orderId === purchase.orderId ? signingTab : defaultSigningTab;
              // Signing stays closed until the buyer opens step 2 themselves.
              const signingOpen =
                orderId === purchase.orderId && vsoSigningOpen;
              const stepTwoOpen =
                String(inlineReview) === String(purchase.orderId);
              const reviewDocument =
                activeTab === "spa" ? signedDocuments.spa : signedDocuments.vso;
              const reviewReadOnly = Boolean(reviewDocument) &&
                !(activeTab === "vso" && vsoNeedsSignature);
              const stepTwoReview = stepTwoOpen && (
                <section className="purchase-inline-review">
                  <Container tag="div"
                    className="purchase-signing-tabs"
                    role="tablist"
                    aria-label="Signed documents"
                  >
                    {signingTabs.map(([value, label, done]) => (
                      <button
                        type="button"
                        role="tab"
                        key={value}
                        aria-selected={activeTab === value}
                        className={`purchase-signing-tab ${activeTab === value ? "active" : ""} ${done ? "is-done" : ""}`}
                        onClick={() => {
                          setOrderId(purchase.orderId);
                          setSigningTab(value);
                          setVsoSigningOpen(true);
                        }}
                      >
                        <Icon
                          icon={
                            done
                              ? "solar:check-circle-bold"
                              : "solar:danger-circle-bold"
                          }
                          className={
                            done ? "_svg signing-done" : "_svg signing-pending"
                          }
                        />
                        {label}
                      </button>
                    ))}
                  </Container>

                  {/* A requested VSO can be signed again even with an older signed copy. */}
                  <VsoSigning
                    key={`review-${activeTab}-${purchase.orderId}-${reviewReadOnly}`}
                    documentType={activeTab}
                    readOnly={reviewReadOnly}
                    documentOnly={reviewReadOnly}
                    document={reviewDocument}
                    purchase={
                      submittedSteps[purchase.orderId]?.identity_details
                        ? {
                            ...purchase,
                            source: {
                              ...purchase.source,
                              identity_details:
                                submittedSteps[purchase.orderId]
                                  .identity_details,
                            },
                          }
                        : purchase
                    }
                    isSubmitting={createLoading}
                    onComplete={(signedDocument, signer) =>
                      activeTab === "spa"
                        ? handleAgreementSubmit(
                            purchase.orderId,
                            signedDocument,
                            signer,
                          )
                        : handleFinanceSubmit(
                            purchase.orderId,
                            signedDocument,
                            signer,
                            buyerTypeOf(purchase),
                          )
                    }
                  />
                </section>
              );
              return (
                <Container tag="div"
                  className="purchase-list-entry"
                  key={`${purchase.orderId || purchase.reservationId}-${index}`}
                >
                  <PurchaseRecord
                    purchase={purchase}
                    onPay={setPaymentOrder}
                    active={isActiveEntry}
                    canViewDetails={isPaid(purchase)}
                    onToggle={() => {
                      if (!isPaid(purchase)) {
                        showToast(
                          "Payment required",
                          "Please pay the reservation fee first to proceed with your purchase steps.",
                          "warning",
                        );
                        return;
                      }
                      setExpandedOrder(
                        isActiveEntry ? "none" : purchase.orderId,
                      );
                    }}
                  />

                  {stage === "delivery" &&
                    isActiveEntry &&
                    steps.some((step) => step.submitted) && (
                      <section className="purchase-task purchase-task-expand vso-open">
                        <PurchaseProgress
                          currentStep={4}
                          steps={progressSteps}
                          onReview={openReview}
                        />

                        {stepTwoReview}
                      </section>
                    )}

                  {isPaid(purchase) &&
                    stage !== "delivery" &&
                    isActiveEntry && (
                      <section
                        className={`purchase-task purchase-task-expand ${
                          stage === "financing" ? "vso-open" : ""
                        }`}
                      >
                        <p className="purchase-task-notice security-note-inline">
                          <Icon
                            tag="span"
                            icon="solar:lock-keyhole-linear"
                            className="security-lock"
                          />
                          {t("Complete your task to avoid delays in delivery")}
                        </p>

                        <PurchaseProgress
                          currentStep={stepTwoOpen ? 2 : stageNumber[stage]}
                          steps={progressSteps}
                          onReview={openReview}
                        />

                        {stepTwoReview}

                        {!stepTwoOpen && (
                          <>
                            <h2 className="_label purchase-task-title">
                              {t(taskCopy[stage].title)}
                            </h2>

                            <p className="_label purchase-task-subtitle">
                              {t(
                                stage === "reserved" && !isPaid(purchase)
                                  ? "Pay the reservation fee first to unlock this step"
                                  : taskCopy[stage].subtitle,
                              )}
                            </p>

                            {stage === "financing" && (
                              <>
                                <section className="purchase-bank-card">
                                  <h3>{t("DCAR Only Bank Account")}</h3>

                                  <dl>
                                    {bankDetails.map(([label, value]) => (
                                      <Container tag="div" key={label}>
                                        <dt>{t(label)}</dt>
                                        <dd>: {value}</dd>
                                      </Container>
                                    ))}

                                    <Container tag="div">
                                      <dt>{t("Bank Account No.")}</dt>
                                      <dd className="purchase-bank-account">
                                        : <span>{bankAccountNo}</span>
                                        <button
                                          type="button"
                                          onClick={copyAccountNo}
                                          aria-label="Copy bank account number"
                                        >
                                          <Icon icon="solar:copy-linear" />
                                        </button>
                                      </dd>
                                    </Container>

                                    <Container tag="div">
                                      <dt>
                                        {buyerTypeOf(purchase) === "loan"
                                          ? t("Down Payment (10%)")
                                          : t("Remaining Balance")}
                                      </dt>
                                      <dd>
                                        :{" "}
                                        {displayMoney(
                                          buyerTypeOf(purchase) === "loan"
                                            ? downPaymentOf(purchase)
                                            : remainingBalanceOf(purchase),
                                        )}
                                      </dd>
                                    </Container>
                                  </dl>
                                </section>

                                <h3 className="purchase-signing-title">
                                  {t("Purchase Agreement & Vehicle Sales Order")}
                                </h3>

                                {signingLocked && (
                                  <p
                                    className="purchase-signing-locked-note"
                                    role="status"
                                  >
                                    <Icon
                                      tag="span"
                                      icon="solar:lock-keyhole-linear"
                                    />
                                    {t(
                                      "VSO signing opens once your documents are ready for signature.",
                                    )}
                                  </p>
                                )}

                                <Container tag="div"
                                  className="purchase-signing-tabs"
                                  role="tablist"
                                  aria-label="Documents to sign"
                                >
                                  {signingTabs.map(([value, label, done]) => (
                                    <button
                                      type="button"
                                      role="tab"
                                      disabled={signingLocked && value === "vso"}
                                      aria-selected={activeTab === value}
                                      className={`purchase-signing-tab ${activeTab === value ? "active" : ""} ${done ? "is-done" : ""} ${signingLocked && value === "vso" ? "is-locked" : ""}`}
                                      key={value}
                                      onClick={() => {
                                        setOrderId(purchase.orderId);
                                        setSigningTab(value);
                                        setVsoSigningOpen(true);
                                      }}
                                    >
                                      {!(signingLocked && value === "vso") && (
                                        <Icon
                                          icon={
                                            done
                                              ? "solar:check-circle-bold"
                                              : "solar:danger-circle-bold"
                                          }
                                          className={
                                            done
                                              ? "_svg signing-done"
                                              : "_svg signing-pending"
                                          }
                                        />
                                      )}
                                      {label}
                                    </button>
                                  ))}
                                </Container>
                              </>
                            )}

                            {stage !== "financing" && (
                              <button
                                type="button"
                                className="purchase-task-action"
                                disabled={
                                  (stage === "reserved" && !isPaid(purchase)) ||
                                  createLoading
                                }
                                onClick={() => {
                                  setOrderId(purchase.orderId);

                                  if (stage === "reserved") {
                                    if (!isPaid(purchase)) return;
                                    setIdentityOpen(true);
                                  }

                                  if (stage === "insurance") setInsuranceOpen(true);
                                }}
                              >
                                {stage === "reserved" && !isPaid(purchase)
                                  ? t("Payment Required")
                                  : t("Start")}
                              </button>
                            )}

                            {stage === "financing" &&
                              // !signingLocked &&
                              activeTab === "spa" &&
                              signingOpen && (
                                <VsoSigning
                                  key={`spa-${purchase.orderId}-${Boolean(signedDocuments.spa)}`}
                                  documentType="spa"
                                  readOnly={Boolean(signedDocuments.spa)}
                                  document={signedDocuments.spa}
                                  documentOnly={Boolean(signedDocuments.spa)}
                                  purchase={
                                    submittedSteps[purchase.orderId]
                                      ?.identity_details
                                      ? {
                                          ...purchase,
                                          source: {
                                            ...purchase.source,
                                            identity_details:
                                              submittedSteps[purchase.orderId]
                                                .identity_details,
                                          },
                                        }
                                      : purchase
                                  }
                                  isSubmitting={createLoading}
                                  onComplete={(signedDocument, signer) =>
                                    handleAgreementSubmit(
                                      purchase.orderId,
                                      signedDocument,
                                      signer,
                                    )
                                  }
                                />
                              )}

                            {stage === "financing" &&
                              !signingLocked &&
                              activeTab === "vso" &&
                              signingOpen && (
                                <VsoSigning
                                  key={`vso-${purchase.orderId}-${Boolean(signedDocuments.vso)}`}
                                  readOnly={Boolean(signedDocuments.vso) && !vsoNeedsSignature}
                                  document={signedDocuments.vso}
                                  documentOnly={Boolean(signedDocuments.vso) && !vsoNeedsSignature}
                                  purchase={
                                    submittedSteps[purchase.orderId]
                                      ?.identity_details
                                      ? {
                                          ...purchase,
                                          source: {
                                            ...purchase.source,
                                            identity_details:
                                              submittedSteps[purchase.orderId]
                                                .identity_details,
                                          },
                                        }
                                      : purchase
                                  }
                                  isSubmitting={createLoading}
                                  onComplete={(signedDocument, signer) =>
                                    handleFinanceSubmit(
                                      purchase.orderId,
                                      signedDocument,
                                      signer,
                                      buyerTypeOf(purchase),
                                    )
                                  }
                                />
                              )}
                          </>
                        )}
                      </section>
                    )}
                </Container>
              );
            })
          )}
        </section>
      </main>

      {identityOpen && (
        <IdentityDetailsModal
          orderId={orderId}
          onClose={() => setIdentityOpen(false)}
          onComplete={(response, values) => {
            saveSubmittedStep(orderId, "identity_details", values);
            setIdentityOpen(false);
            moveToStage("financing");
          }}
        />
      )}

      {insuranceOpen && (
        <InsuranceRequestModal
          orderId={orderId}
          sellingPrice={
            normalizedPurchases.find(
              (purchase) => String(purchase.orderId) === String(orderId),
            )?.sellingPriceValue
          }
          onClose={() => setInsuranceOpen(false)}
          onComplete={(response, values) => {
            saveSubmittedStep(orderId, "insurance", values);
            setInsuranceOpen(false);
            moveToStage("delivery");
          }}
        />
      )}

      {review && (
        <PurchaseStepReviewModal
          purchase={review.purchase}
          step={
            getSubmittedPurchaseSteps(
              review.purchase,
              submittedSteps[review.purchase.orderId],
            )[review.number - 1]
          }
          onClose={() => setReview(null)}
        />
      )}

      {paymentOrder && (
        <PaymentMethodModal
          open
          order={paymentOrder}
          onClose={() => setPaymentOrder(null)}
          onSuccess={() => refreshOrders(false)}
        />
      )}
    </>
  );
}
